import { useState } from 'react';
import { useAuth } from '../lib/auth-context';
import { supabase } from '../lib/supabase';
import { createCheckoutSession, createPortalSession } from '../lib/api';
import { BILLING_PROFILE_COLUMNS, loadBillingProfile, planAction, requestBillingRedirect } from '../lib/billing-plans';
import type { BillingProfile, BillingRedirectResult, BillingRowFetcher, PlanAction, PlanCard } from '../lib/billing-plans';
import useSWR from 'swr';

interface PricingTier extends PlanCard {
  readonly name: string;
  readonly price: string;
  readonly features: readonly string[];
}

const PRICING_TIERS: readonly PricingTier[] = [
  {
    name: 'Explorer',
    price: 'Free',
    tier: 'explorer',
    features: ['4 core tools', '10 requests/min', '100 requests/day'],
  },
  {
    name: 'Professional',
    price: '$149/mo',
    tier: 'professional',
    features: ['7 tools', '60 requests/min', '5,000 requests/day', 'Alert notifications'],
    // Stripe TEST-MODE price (REQ-2026-07-05-001). Must stay in sync with
    // STRIPE_PRICE_TIER_MAP on the stripe-webhooks worker + Pages functions.
    // Production launch needs live-mode price IDs here.
    priceId: 'price_1TpwGd4NBQlZO2rkwPC9buQf',
  },
  {
    name: 'Team',
    price: '$499/mo',
    tier: 'team',
    features: ['10 tools', '120 requests/min', '20,000 requests/day', 'Advanced alerts'],
    priceId: 'price_1TpwGd4NBQlZO2rkuKBUUbWc', // Stripe TEST-MODE price (team)
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    tier: 'enterprise',
    features: ['12 tools', '300 requests/min', '100,000 requests/day', 'Dedicated support'],
  },
];

const fetchBillingRow: BillingRowFetcher = (tenantId) =>
  supabase.from('tenants').select(BILLING_PROFILE_COLUMNS).eq('tenant_id', tenantId).single();

interface PlanButtonProps {
  readonly action: PlanAction;
  readonly disabled: boolean;
  readonly onCheckout: () => void;
  readonly onPortal: () => void;
}

function PlanButton({ action, disabled, onCheckout, onPortal }: PlanButtonProps) {
  const buttonClass =
    'w-full py-2 px-4 border border-sentinel-700 text-sentinel-700 rounded-md hover:bg-sentinel-50 disabled:opacity-50 text-sm';
  switch (action.kind) {
    case 'current':
      return <span className="block text-center text-sm text-sentinel-700 font-medium">Current Plan</span>;
    case 'checkout':
      return (
        <button onClick={onCheckout} disabled={disabled} className={buttonClass}>
          {action.label}
        </button>
      );
    case 'portal':
      // Existing subscribers change plans in the Stripe billing portal, never
      // through a second checkout (BUG-021 #1).
      return (
        <button onClick={onPortal} disabled={disabled} className={buttonClass}>
          {action.label}
        </button>
      );
    case 'contact_sales':
      return (
        <a
          href="mailto:sales@gxpsentinel.com"
          className="block text-center py-2 px-4 border border-gray-300 text-gray-600 rounded-md hover:bg-gray-50 text-sm"
        >
          Contact Sales
        </a>
      );
    case 'none':
      return null;
  }
}

export function BillingPage() {
  const { user, session } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: profile, error: profileError } = useSWR<BillingProfile, Error, readonly [string, string] | null>(
    user ? ['billing-profile', user.id] : null,
    ([, tenantId]) => loadBillingProfile(fetchBillingRow, tenantId),
  );

  const follow = (result: BillingRedirectResult): void => {
    if (result.kind === 'redirect') {
      window.location.href = result.url;
      return;
    }
    setError(result.kind === 'error' ? result.message : 'You already have a subscription. Opening the billing portal.');
    setLoading(false);
  };

  const handlePortal = async (): Promise<void> => {
    if (!session) return;
    setLoading(true);
    setError(null);
    follow(await requestBillingRedirect(() => createPortalSession(session.access_token)));
  };

  const handleCheckout = async (priceId: string): Promise<void> => {
    if (!session) return;
    setLoading(true);
    setError(null);
    const result = await requestBillingRedirect(() => createCheckoutSession(session.access_token, priceId));
    if (result.kind === 'subscription_exists') {
      // The server found a live subscription (e.g. a webhook still in flight):
      // change plans in the portal instead of starting a second subscription.
      await handlePortal();
      return;
    }
    follow(result);
  };

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">Billing</h2>

      {error && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded text-sm">{error}</div>}

      {profileError && (
        <div role="alert" className="mb-4 p-3 bg-red-50 text-red-700 rounded text-sm">
          {profileError.message}
        </div>
      )}

      {profile?.paymentPastDue && (
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded text-yellow-800">
          Your payment is past due. Please update your payment method to avoid service interruption.
        </div>
      )}

      {profile?.hasBillingAccount && (
        <div className="mb-8">
          <button
            onClick={() => void handlePortal()}
            disabled={loading}
            className="px-4 py-2 bg-sentinel-700 text-white rounded-md hover:bg-sentinel-900 disabled:opacity-50"
          >
            Manage Subscription
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {PRICING_TIERS.map((plan) => (
          <div
            key={plan.tier}
            className={`bg-white rounded-lg shadow p-6 ${profile?.tier === plan.tier ? 'ring-2 ring-sentinel-500' : ''}`}
          >
            <h3 className="text-lg font-semibold">{plan.name}</h3>
            <p className="text-2xl font-bold mt-2 mb-4">{plan.price}</p>
            <ul className="space-y-2 text-sm text-gray-600 mb-6">
              {plan.features.map((f) => (
                <li key={f}>✓ {f}</li>
              ))}
            </ul>
            {/* No plan actions until the profile has loaded: an unknown tier must
                never render as "explorer with Upgrade everywhere" (BUG-021 #2). */}
            {profile && (
              <PlanButton
                action={planAction(plan, profile)}
                disabled={loading}
                onCheckout={() => {
                  if (plan.priceId) void handleCheckout(plan.priceId);
                }}
                onPortal={() => void handlePortal()}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
